import { z } from 'zod'

import {
  BoardColumnConfig,
  BoardColumnConfigSchema,
  ColumnMappingTypeEnum,
  createStatusTypeMapping,
} from './board-column'
import { TaskStatusTypeEnum } from './task-status'

// =====================
// Workstream workspaces: user-created containers whose TRACKED members' open
// tasks are aggregated into the Workstream summary/list/board.
// =====================

export enum WorkstreamRoleEnum {
  /** Work is aggregated; can also view the workstream */
  TRACKED = 'TRACKED',
  /** Can edit workstream config + members */
  MANAGER = 'MANAGER',
  /** Read-only observer */
  VIEWER = 'VIEWER',
}

// ---------- CRUD request schemas (parsed in controllers) ----------

export const WorkstreamMemberInputSchema = z.object({
  memberId: z.string().uuid(),
  role: z
    .nativeEnum(WorkstreamRoleEnum)
    .default(WorkstreamRoleEnum.TRACKED),
})

export type WorkstreamMemberInput = z.infer<typeof WorkstreamMemberInputSchema>

export const CreateWorkstreamSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2000).optional().nullable(),
  members: z.array(WorkstreamMemberInputSchema).optional(),
})

export type CreateWorkstreamInput = z.infer<typeof CreateWorkstreamSchema>

export const UpdateWorkstreamSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().max(2000).optional().nullable(),
  visibilityMode: z.enum(['team', 'restricted']).optional(),
})

export type UpdateWorkstreamInput = z.infer<typeof UpdateWorkstreamSchema>

export const AddWorkstreamMembersSchema = z.union([
  WorkstreamMemberInputSchema,
  z.array(WorkstreamMemberInputSchema).min(1),
])

export const UpdateWorkstreamMemberSchema = z.object({
  role: z.nativeEnum(WorkstreamRoleEnum),
})

export type UpdateWorkstreamMemberInput = z.infer<
  typeof UpdateWorkstreamMemberSchema
>

// ---------- Board settings (stored in workstream.settings.boardSettings) ----------

export const WorkstreamBoardSettingsSchema = z.object({
  columns: z.array(BoardColumnConfigSchema).default([]),
  defaultColumnId: z.string().optional(),
  wipLimits: z.record(z.string(), z.number()).optional(),
})

export type WorkstreamBoardSettings = z.infer<
  typeof WorkstreamBoardSettingsSchema
>

/**
 * Cross-project defaults: STATUS_TYPE rules only, so every task's own project
 * status set can resolve a concrete target status on drop.
 */
export const DEFAULT_WORKSTREAM_BOARD_COLUMNS: BoardColumnConfig[] = [
  {
    id: 'todo',
    name: 'To Do',
    color: '#E5E7EB',
    order: 1,
    mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.NOT_STARTED)],
  },
  {
    id: 'in-progress',
    name: 'In Progress',
    color: '#DBEAFE',
    order: 2,
    mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.IN_PROGRESS)],
  },
  {
    id: 'on-hold',
    name: 'On Hold',
    color: '#FEF3C7',
    order: 3,
    mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.ON_HOLD)],
  },
  {
    id: 'done',
    name: 'Done',
    color: '#D1FAE5',
    order: 4,
    mappingRules: [createStatusTypeMapping(TaskStatusTypeEnum.COMPLETED)],
  },
]

/**
 * Normalize possibly-partial/legacy settings JSON into a usable board config.
 * Falls back to the cross-project defaults when nothing is stored.
 */
export function normalizeWorkstreamBoardSettings(
  settings?: unknown,
): WorkstreamBoardSettings {
  const parsed = WorkstreamBoardSettingsSchema.safeParse(settings ?? {})
  if (!parsed.success || parsed.data.columns.length === 0) {
    return {
      columns: DEFAULT_WORKSTREAM_BOARD_COLUMNS.map(column => ({
        ...column,
        mappingRules: column.mappingRules.map(rule => ({ ...rule })),
      })),
      defaultColumnId: 'todo',
    }
  }
  return {
    ...parsed.data,
    columns: [...parsed.data.columns].sort((a, b) => a.order - b.order),
  }
}

/** Rule types that make sense on a cross-project board (STATUS_ID is per-project). */
export const WORKSTREAM_BOARD_RULE_TYPES = [
  ColumnMappingTypeEnum.STATUS_TYPE,
  ColumnMappingTypeEnum.STATUS_NAME,
] as const

// ---------- Membership rules (stored in workstream.settings.membershipRules) ----------
//
// A workstream's task set is defined by a query: a flat list of sources OR'd
// together, each source an AND of simple conditions. No nesting — the sources
// list keeps "why is this task here" answerable. A workstream with no stored
// rules behaves as the single tracked-members source (the v2 person-lens).

// List-valued conditions match as OR within a field (a task matching ANY of the
// listed projects/tags/types satisfies that field) while distinct fields still
// AND together, and sources still OR. assigneeMemberIds was list-valued from the
// start; project/tag/type/group joined it so a single source can span several.
const WorkstreamSourceConditionsRawSchema = z
  .object({
    /** Dynamic token: tasks assigned to this workstream's TRACKED members */
    assignedToTrackedMembers: z.literal(true).optional(),
    /** Tasks assigned to any of these specific org members */
    assigneeMemberIds: z.array(z.string().uuid()).min(1).max(50).optional(),
    /** Tasks in any of these projects (moduleType PROJECT) */
    projectIds: z.array(z.string().uuid()).min(1).max(50).optional(),
    /** Tasks in any of these task groups; only meaningful with projectIds */
    taskGroupIds: z.array(z.string().uuid()).min(1).max(50).optional(),
    /** Tasks carrying any of these org-wide tags */
    tagIds: z.array(z.string().uuid()).min(1).max(50).optional(),
    /** Tasks of any of these task types */
    taskTypeDefIds: z.array(z.string().uuid()).min(1).max(50).optional(),
    /** Only tasks with no assignee */
    unassignedOnly: z.literal(true).optional(),
  })
  .refine(c => Object.values(c).some(v => v !== undefined), {
    message: 'A source needs at least one condition',
  })
  .refine(c => !(c.taskGroupIds?.length && !c.projectIds?.length), {
    message: 'A task group condition needs a project',
  })
  .refine(
    c =>
      !(
        c.unassignedOnly &&
        (c.assignedToTrackedMembers || c.assigneeMemberIds?.length)
      ),
    { message: 'A source cannot require both an assignee and no assignee' },
  )

/**
 * Rules stored before fields went multi-valued used singular keys (projectId,
 * tagId, taskTypeDefId, taskGroupId). Upgrade them to the array form before
 * validation so saved workstreams keep matching without a data migration.
 */
export const WorkstreamSourceConditionsSchema = z.preprocess(value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value
  const c: Record<string, unknown> = { ...(value as Record<string, unknown>) }
  const upgrade = (singular: string, plural: string) => {
    if (c[singular] != null && c[plural] == null) c[plural] = [c[singular]]
    delete c[singular]
  }
  upgrade('projectId', 'projectIds')
  upgrade('taskGroupId', 'taskGroupIds')
  upgrade('tagId', 'tagIds')
  upgrade('taskTypeDefId', 'taskTypeDefIds')
  return c
}, WorkstreamSourceConditionsRawSchema)

export type WorkstreamSourceConditions = z.infer<
  typeof WorkstreamSourceConditionsSchema
>

export const WorkstreamMembershipSourceSchema = z.object({
  /** Client-generated key for stable editing; not semantic */
  id: z.string().max(50).optional(),
  conditions: WorkstreamSourceConditionsSchema,
})

export type WorkstreamMembershipSource = z.infer<
  typeof WorkstreamMembershipSourceSchema
>

export const WorkstreamMembershipRulesSchema = z.object({
  sources: z.array(WorkstreamMembershipSourceSchema).min(1).max(20),
})

export type WorkstreamMembershipRules = z.infer<
  typeof WorkstreamMembershipRulesSchema
>

export const DEFAULT_WORKSTREAM_MEMBERSHIP_RULES: WorkstreamMembershipRules = {
  sources: [{ id: 'tracked-members', conditions: { assignedToTrackedMembers: true } }],
}

/**
 * Normalize possibly-absent/legacy settings JSON into usable membership rules.
 * Absent or invalid rules fall back to the tracked-members source, which is
 * exactly the pre-rules behavior — existing workstreams change zero percent.
 */
export function normalizeWorkstreamMembershipRules(
  settings?: unknown,
): WorkstreamMembershipRules {
  const raw =
    settings && typeof settings === 'object'
      ? (settings as Record<string, unknown>).membershipRules
      : undefined
  const parsed = WorkstreamMembershipRulesSchema.safeParse(raw)
  if (!parsed.success) {
    return {
      sources: DEFAULT_WORKSTREAM_MEMBERSHIP_RULES.sources.map(s => ({
        ...s,
        conditions: { ...s.conditions },
      })),
    }
  }
  return parsed.data
}

/** Whether stored settings carry explicit (non-default) membership rules. */
export function hasCustomMembershipRules(settings?: unknown): boolean {
  const raw =
    settings && typeof settings === 'object'
      ? (settings as Record<string, unknown>).membershipRules
      : undefined
  return WorkstreamMembershipRulesSchema.safeParse(raw).success
}

// ---------- Visibility mode (stored in workstream.settings.visibilityMode) ----------
//
// team: every viewer of the workstream sees all matched work, including tasks
// from projects they can't otherwise open (the v2 default — projects own the
// work, workstreams own the team). restricted: each viewer sees only work from
// projects they can access, so totals are per-viewer and may be partial.

export const WorkstreamVisibilityModeSchema = z.enum(['team', 'restricted'])

export type WorkstreamVisibilityMode = z.infer<
  typeof WorkstreamVisibilityModeSchema
>

export const DEFAULT_WORKSTREAM_VISIBILITY_MODE: WorkstreamVisibilityMode =
  'team'

/** Absent or unrecognized settings fall back to the team default. */
export function normalizeWorkstreamVisibilityMode(
  settings?: unknown,
): WorkstreamVisibilityMode {
  const raw =
    settings && typeof settings === 'object'
      ? (settings as Record<string, unknown>).visibilityMode
      : undefined
  return raw === 'restricted' ? 'restricted' : DEFAULT_WORKSTREAM_VISIBILITY_MODE
}

// ---------- Manual queue order (workstream_task_ranks) ----------
//
// The member-grouped list sorts by due date then priority. A manager can
// override that per member by dragging rows, which writes a rank scoped to
// (workstream, lane, task). A "lane" is a tracked member id, or the literal
// UNASSIGNED_LANE_KEY bucket for tasks matching no tracked member. A task under
// two members carries an independent rank in each lane — the two rows sequence
// two different people's days.

/** Lane key for the "Unassigned to tracked members" group. */
export const UNASSIGNED_LANE_KEY = 'unassigned'

/**
 * A drag writes the whole lane, not just the moved row. Ranked tasks sort above
 * unranked ones, so dropping a task between two unranked neighbours in a
 * partly-ranked lane would otherwise fling it to the top instead of leaving it
 * where it was released. Restating the lane in its resulting visual order makes
 * the drop mean exactly what it looked like.
 */
export const WorkstreamLaneOrderSchema = z.object({
  /** A tracked member id, or UNASSIGNED_LANE_KEY */
  laneKey: z.string().trim().min(1).max(64),
  /** The lane's tasks in their new order; positions are fractional index keys */
  positions: z
    .array(
      z.object({
        taskId: z.string().uuid(),
        position: z.string().trim().min(1).max(64),
      }),
    )
    .min(1)
    .max(500),
})

export type WorkstreamLaneOrderInput = z.infer<typeof WorkstreamLaneOrderSchema>

/** laneKey -> taskId -> fractional position. Lanes with no manual order are absent. */
export type WorkstreamTaskRanks = Record<string, Record<string, string>>

// ---------- Response DTOs ----------

export interface WorkstreamMemberDto {
  id: string
  memberId: string
  userId: string | null
  displayName: string
  email: string | null
  image: string | null
  jobTitle: string | null
  role: WorkstreamRoleEnum
}

export interface WorkstreamListItem {
  id: string
  name: string
  description: string | null
  memberCount: number
  trackedCount: number
  /** The caller's role in this workstream; null for org admins who aren't members */
  myRole: WorkstreamRoleEnum | null
  updatedAt: string
}

export interface WorkstreamDetail extends WorkstreamListItem {
  settings: Record<string, unknown>
  createdBy: string | null
  members: WorkstreamMemberDto[]
}

// ---------- Summary DTOs (aggregation over TRACKED members' open tasks) ----------

export type WorkstreamBlockedReason = 'DEPENDENCY' | 'ON_HOLD'

export interface WorkstreamMemberSummary {
  memberId: string
  userId: string | null
  displayName: string
  image: string | null
  jobTitle: string | null
  openCount: number
  inProgressCount: number
  blockedCount: number
  overdueCount: number
}

export interface WorkstreamClientSummary {
  /** null for the "Internal / no client" bucket */
  entityId: string | null
  name: string
  taskCount: number
  blockedCount: number
  projectIds: string[]
}

export interface WorkstreamBlockerRef {
  taskId: string
  taskNumber: string | null
  name: string
  statusTypeId: string | null
  dependencyType: string
}

export interface WorkstreamBlockedTask {
  id: string
  taskNumber: string | null
  name: string
  priority: string
  dueDate: string | null
  entityId: string | null
  projectId: string | null
  assigneeMemberIds: string[]
  reason: WorkstreamBlockedReason
  blockers: WorkstreamBlockerRef[]
}

export interface WorkstreamSummary {
  generatedAt: string
  taskCount: number
  truncated: boolean
  members: WorkstreamMemberSummary[]
  clients: WorkstreamClientSummary[]
  blockedTasks: WorkstreamBlockedTask[]
}

// ---------- Flow surface (GET /workstreams/:id/flow) ----------
//
// Queue-shaped metrics over the membership query: what finished, how fast
// work moves, what's sitting. Workstream-scoped and unranked by design — no
// per-member leaderboards.

export const WORKSTREAM_FLOW_WINDOWS = [7, 14, 30] as const

export type WorkstreamFlowWindowDays = (typeof WORKSTREAM_FLOW_WINDOWS)[number]

export const DEFAULT_WORKSTREAM_FLOW_WINDOW: WorkstreamFlowWindowDays = 14

export function parseWorkstreamFlowWindow(
  value: unknown,
): WorkstreamFlowWindowDays {
  const parsed = Number(value)
  return (WORKSTREAM_FLOW_WINDOWS as readonly number[]).includes(parsed)
    ? (parsed as WorkstreamFlowWindowDays)
    : DEFAULT_WORKSTREAM_FLOW_WINDOW
}

/** Where a flow task lives; all null for personal/other (non-project) tasks */
export interface WorkstreamFlowProjectRef {
  projectId: string | null
  projectName: string | null
  entityId: string | null
  entityName: string | null
}

export interface WorkstreamFlowCompletion {
  id: string
  taskNumber: string | null
  name: string
  completedAt: string
  completerName: string | null
}

export interface WorkstreamFlowDigestGroup extends WorkstreamFlowProjectRef {
  tasks: WorkstreamFlowCompletion[]
}

export interface WorkstreamFlowThroughputBucket {
  /** ISO date of the bucket's first day; buckets are 7 days, oldest first */
  weekStart: string
  count: number
}

export interface WorkstreamFlowCycleTime {
  medianHours: number | null
  p90Hours: number | null
  /** Window completions that carried a usable start signal */
  sampleCount: number
  /** Window completions with no start signal, excluded from the stats */
  excludedCount: number
}

/** Display-only assignee ref for flow queue rows; agent assignees omitted */
export interface WorkstreamFlowAssignee {
  memberId: string | null
  userId: string | null
  displayName: string
  image: string | null
}

export interface WorkstreamFlowAgingTask extends WorkstreamFlowProjectRef {
  id: string
  taskNumber: string | null
  name: string
  /** When the task was recorded as started; null when never stamped */
  startedAt: string | null
  /**
   * Days in progress. Derived from startedAt when present, else from the
   * task's last-updated time as a best-effort fallback (startedAt is only
   * stamped on transitions through the status endpoint).
   */
  ageDays: number
  blocked: boolean
  blockedTimeHours: number | null
  assignees: WorkstreamFlowAssignee[]
}

export interface WorkstreamFlowReviewTask extends WorkstreamFlowProjectRef {
  id: string
  taskNumber: string | null
  name: string
  reviewRequestedAt: string | null
  assignees: WorkstreamFlowAssignee[]
}

export interface WorkstreamFlow {
  generatedAt: string
  windowDays: WorkstreamFlowWindowDays
  /** Completions inside the window (digest scope, before any cap) */
  completedCount: number
  /** The completions query hit its cap; older buckets may undercount */
  truncated: boolean
  digest: WorkstreamFlowDigestGroup[]
  throughput: WorkstreamFlowThroughputBucket[]
  cycleTime: WorkstreamFlowCycleTime
  aging: WorkstreamFlowAgingTask[]
  reviewQueue: WorkstreamFlowReviewTask[]
}
